// Module 09 §8 — the dashboard-only landing resolver (ledger B9). Pure: no Vue, no store, no
// http, and deliberately no `vue-router` import either, so the resolver is testable in complete
// isolation and 05's `permissionGuard` composes it without a back-import in either direction.
//
// Ports legacy `dashboardStore.getValidFirstDashboard:35-56` and `buildCustomDashboardRoute:59-79`.

/** A by-name route target, structurally assignable to vue-router's `RouteLocationRaw`. */
export interface DashboardRouteTarget {
  name: string
  params?: Record<string, string>
}

/** The four routes module 09 owns (contract §3.1). */
export const DASHBOARD_ROUTE_NAMES: readonly string[] = [
  'CustomDashboard',
  'CustomDashboardCreate',
  'CustomDashboardLegacy',
  'AccountCenter',
]

const DASHBOARD_ROUTE_NAME_SET = new Set(DASHBOARD_ROUTE_NAMES)

export const CREATE_DASHBOARD_ROUTE: DashboardRouteTarget = { name: 'CustomDashboardCreate' }

/**
 * Is `to` one of module 09's routes?
 *
 * 05's `permissionGuard` composes this with `useRbac().onlyDashboard` (07). Note the legacy
 * guard (`router/index.js:87-92`) also exempted `GetStarted` — that is an onboarding route, not
 * a dashboard route, so it stays 05's to exempt alongside this predicate.
 */
export function isDashboardRoute(to: { name?: unknown }): boolean {
  return typeof to.name === 'string' && DASHBOARD_ROUTE_NAME_SET.has(to.name)
}

/** The minimum a dashboard needs to be routable. */
export interface RoutableDashboard {
  url_id?: string | null
  workspace_id?: string | number | null
  account_id?: string | number | null
}

/**
 * The ids the resolver falls back through when a dashboard does not carry its own.
 *
 * In practice it always falls through: the backend's dashboard DTO
 * (`serialize_custom_dashboard_definition`) emits no `account_id` at all. The legacy order is
 * preserved verbatim anyway — it is the behaviour under test, not an accident to tidy.
 */
export interface DashboardRouteContext {
  accountId?: string | number | null
  workspaceId?: string | number | null
  /** `workspaceStore.workspace?.account_id` — the last account fallback before `0`. */
  workspaceAccountId?: string | number | null
}

/** Legacy `a || b || c || 0` semantics: `0`, `''` and `null` all fall through. */
function firstTruthyId(...candidates: (string | number | null | undefined)[]): string {
  for (const candidate of candidates) {
    if (candidate) return String(candidate)
  }
  return '0'
}

/**
 * The route for one dashboard, or the create route when it cannot be linked.
 * Ports `buildCustomDashboardRoute:59-79`.
 */
export function buildCustomDashboardRoute(
  dashboard: RoutableDashboard | null | undefined,
  context: DashboardRouteContext = {},
): DashboardRouteTarget {
  if (!dashboard?.url_id) return CREATE_DASHBOARD_ROUTE

  return {
    name: 'CustomDashboard',
    params: {
      account_id: firstTruthyId(
        dashboard.account_id,
        context.accountId,
        context.workspaceAccountId,
      ),
      workspace_id: firstTruthyId(dashboard.workspace_id, context.workspaceId),
      url: dashboard.url_id,
    },
  }
}

/**
 * The route for one account center. Its record is `account-center/:url?` (contract §11), so the
 * param shape is NOT the dashboard one — `buildCustomDashboardRoute` would emit a
 * `CustomDashboard` target with `account_id`/`workspace_id` params the route does not declare.
 * With no `url_id` this yields the bare surface, which the optional param allows.
 */
export function buildAccountCenterRoute(
  accountCenter: Pick<RoutableDashboard, 'url_id'> | null | undefined,
): DashboardRouteTarget {
  return accountCenter?.url_id
    ? { name: 'AccountCenter', params: { url: accountCenter.url_id } }
    : { name: 'AccountCenter' }
}

/**
 * Where an `onlyDashboard` user lands: the first dashboard, or the create route when there is
 * none. Ports `getValidFirstDashboard:35-56`; surfaced reactively by `useDashboards()`.
 */
export function firstDashboardRoute(
  dashboards: readonly RoutableDashboard[] | null | undefined,
  context: DashboardRouteContext = {},
): DashboardRouteTarget {
  return buildCustomDashboardRoute(dashboards?.[0], context)
}
